/**
 * 配置校验与归一化（骨架，#89098）
 */

import {
    type CloudRuntimeConfig,
    WorkBuddyCloudConfigError,
    type WorkBuddyCloudOptions,
} from './types.js';

/** publishableKey 形态：`wbpk_{appId}_{随机}`（设计文档 §5.2）。 */
const PUBLISHABLE_KEY_PATTERN = /^wbpk_/;

function normalizeHttpUrl(value: unknown, field: string): string {
    if (typeof value !== 'string' || value.trim() === '') {
        throw new WorkBuddyCloudConfigError(
            `${field} is required and must be a non-empty string.`,
        );
    }

    const trimmed = value.trim();

    let parsed: URL;
    try {
        parsed = new URL(trimmed);
    } catch {
        throw new WorkBuddyCloudConfigError(
            `${field} must be an absolute URL, received: ${trimmed}`,
        );
    }

    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        throw new WorkBuddyCloudConfigError(
            `${field} must use http(s), received protocol: ${parsed.protocol}`,
        );
    }

    // 去掉尾斜杠，后续模块统一用 `${endpoint}${modulePath}` 拼接，避免出现 `//`。
    return trimmed.replace(/\/+$/, '');
}

/**
 * 归一化 endpoint；省略时回落到当前页面同源。
 *
 * 为什么允许省略（而且这是推荐用法）：数据面 `/.cloud/**` 与应用页面**本就同域**
 * （见架构文档「② 生成应用的终端用户读写」），所以同源就是正确答案。而显式传入
 * 一个绝对地址反而引入了出错空间 —— 服务端下发的 endpoint 会被写进前端产物，应用
 * 重新发布 / 追加认证域名后拿到新域名，产物里的旧地址却不会变，SDK 于是把请求发到
 * 旧域名上；数据面按 Origin 精确匹配（服务端 CredentialVerifier）必然拒绝，现场
 * 表现为跨域预检失败，极难归因。同源没有这个失效模式。
 *
 * 仍保留显式传入的能力：本地开发时页面跑在 vite dev server 上、数据面在别处，
 * 以及非浏览器运行时（无 location）必须显式给。
 */
function resolveEndpoint(value: unknown): string {
    if (value !== undefined && value !== null && value !== '') {
        return normalizeHttpUrl(value, 'endpoint');
    }

    // 同源回落：只取 origin，不带 path —— 后续模块统一用 `${endpoint}${modulePath}`
    // 拼接，带上 path 会拼出 /some/page/.cloud/... 这种不存在的地址。
    const origin = globalThis.location?.origin;
    if (typeof origin !== 'string' || origin === '') {
        throw new WorkBuddyCloudConfigError(
            'endpoint is required outside browsers (no location.origin to fall back to).',
        );
    }
    return origin.replace(/\/+$/, '');
}

function normalizePublishableKey(publishableKey: unknown): string {    if (typeof publishableKey !== 'string' || publishableKey.trim() === '') {
        throw new WorkBuddyCloudConfigError(
            'publishableKey is required and must be a non-empty string.',
        );
    }

    const trimmed = publishableKey.trim();
    if (!PUBLISHABLE_KEY_PATTERN.test(trimmed)) {
        // 只校验前缀，不校验长度/字符集 —— 随机段格式由服务端签发决定，SDK 不该跟死。
        throw new WorkBuddyCloudConfigError('publishableKey must start with "wbpk_".');
    }

    return trimmed;
}

function resolveFetch(candidate: WorkBuddyCloudOptions['fetch']): typeof globalThis.fetch {
    if (candidate) {
        return candidate;
    }

    if (typeof globalThis.fetch !== 'function') {
        throw new WorkBuddyCloudConfigError(
            'Global fetch is unavailable in this runtime; pass options.fetch explicitly.',
        );
    }

    // 绑定回 globalThis：某些运行时下裸取 fetch 再调用会丢 this（Illegal invocation）。
    return globalThis.fetch.bind(globalThis);
}

/** 校验并归一化用户传入的配置。任何一项不合法都会抛 {@link WorkBuddyCloudConfigError}。 */
export function resolveRuntimeConfig(options: WorkBuddyCloudOptions): CloudRuntimeConfig {
    if (!options || typeof options !== 'object') {
        throw new WorkBuddyCloudConfigError(
            'createWorkBuddyCloud requires publishableKey.',
        );
    }

    return Object.freeze({
        endpoint: resolveEndpoint(options.endpoint),
        oauthRelayBaseUrl: options.oauthRelayBaseUrl
            ? normalizeHttpUrl(options.oauthRelayBaseUrl, 'oauthRelayBaseUrl')
            : '',
        publishableKey: normalizePublishableKey(options.publishableKey),
        fetch: resolveFetch(options.fetch),
    });
}
